import React, { useMemo, useRef, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView, Platform } from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';
import { AnchoredDropdown } from '@/components/common/admin/desktop/dropdown/AnchoredDropdown';
import { AnchorRect } from '@/hooks/common/useAnchoredPosition';

export interface MultiOption {
  label: string;
  value: string;
}

interface DesktopMultiSelectDropdownProps {
  label: string;
  options: MultiOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  required?: boolean;
  error?: string;
}

export const DesktopMultiSelectDropdown: React.FC<DesktopMultiSelectDropdownProps> = ({
  label,
  options,
  selectedValues,
  onChange,
  placeholder = 'Select',
  required,
  error,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [anchor, setAnchor] = useState<AnchorRect | null>(null);
  const anchorRef = useRef<any>(null);

  const summary = useMemo(() => {
    if (!selectedValues || selectedValues.length === 0) return '';
    const first = options.find((o) => o.value === selectedValues[0])?.label || '';
    if (selectedValues.length === 1) return first;
    return `${first} +${selectedValues.length - 1}`;
  }, [options, selectedValues]);

  const toggleValue = (value: string) => {
    const set = new Set(selectedValues);
    if (set.has(value)) set.delete(value); else set.add(value);
    onChange(Array.from(set));
  };

  const onPress = () => {
    if (Platform.OS === 'web' && anchorRef.current?.getBoundingClientRect) {
      const rect = anchorRef.current.getBoundingClientRect();
      setAnchor({ x: rect.left, y: rect.top, w: rect.width, h: rect.height });
      setIsOpen(true);
      return;
    }
    setAnchor({ x: 24, y: 120, w: 320, h: 44 });
    setIsOpen(true);
  };

  return (
    <View style={styles.container}>
      <Text style={styles.label}>
        {label} {required ? <Text style={styles.required}>*</Text> : null}
      </Text>
      <TouchableOpacity
        ref={anchorRef}
        style={[styles.selector, error && styles.selectorError]}
        onPress={onPress}
      >
        <Text numberOfLines={1} style={[styles.selectedText, !summary && styles.placeholderText]}>
          {summary || placeholder}
        </Text>
        <SVG_COMPONENTS.CHEVRON_DOWN width={18} height={18} color={ADMIN_COLORS.TEXT_SECONDARY} />
      </TouchableOpacity>
      {error ? <Text style={styles.errorText}>{error}</Text> : null}

      <AnchoredDropdown
        isOpen={isOpen}
        anchor={anchor}
        width={anchor?.w ?? 320}
        maxHeight={360}
        onRequestClose={() => setIsOpen(false)}
      >
        <ScrollView style={{ maxHeight: 360 }} contentContainerStyle={{ paddingVertical: 6 }}>
          {options.map((opt) => {
            const active = selectedValues.includes(opt.value);
            return (
              <TouchableOpacity
                key={`opt-${opt.value}`}
                style={[styles.optionRow, active && styles.optionRowActive]}
                onPress={() => toggleValue(opt.value)}
              >
                <View style={[styles.checkbox, active && styles.checkboxChecked]}>
                  {active && (
                    <SVG_COMPONENTS.CHECK_CIRCLE width={12} height={12} color={'#fff'} />
                  )}
                </View>
                <Text style={[styles.optionText, active && styles.optionTextActive]}>{opt.label}</Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      </AnchoredDropdown>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 16,
  },
  label: {
    fontSize: 16,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginBottom: 8,
  },
  required: {
    color: ADMIN_COLORS.ERROR,
  },
  selector: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    paddingHorizontal: 12,
    paddingVertical: 12,
    minHeight: 48,
  },
  selectorError: {
    borderColor: ADMIN_COLORS.ERROR,
  },
  selectedText: {
    flex: 1,
    fontSize: 16,
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginRight: 8,
  },
  placeholderText: {
    color: ADMIN_COLORS.TEXT_TERTIARY,
  },
  errorText: {
    fontSize: 14,
    color: ADMIN_COLORS.ERROR,
    marginTop: 4,
  },
  optionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  optionRowActive: {
    backgroundColor: `${ADMIN_COLORS.PRIMARY}10`,
  },
  optionText: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  optionTextActive: {
    color: ADMIN_COLORS.PRIMARY,
    fontWeight: '600',
  },
  checkbox: {
    width: 18,
    height: 18,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  checkboxChecked: {
    backgroundColor: ADMIN_COLORS.PRIMARY,
    borderColor: ADMIN_COLORS.PRIMARY,
  },
});


